package fr.manyverse.app.domain.validation

import fr.manyverse.app.domain.error.ValidationError

/**
 * Validation des éléments de fiche de personnage (jauges, caractéristiques, compétences, pouvoirs).
 * Chaque méthode renvoie un [Result] sans jamais lever d'exception.
 */
object CharacterSheetValidator {

    const val MAX_GAUGE_LABEL_LENGTH = 50
    const val MAX_CHARACTERISTIC_LABEL_LENGTH = 50
    const val MAX_CHARACTERISTIC_VALUE_DIGITS = 12
    const val MAX_SKILL_LABEL_LENGTH = 100
    const val MAX_POWER_LABEL_LENGTH = 100

    /** Description facultative attachée à une caractéristique, une compétence ou un pouvoir. */
    const val MAX_ENTRY_DESCRIPTION_LENGTH = 200

    /** Valeur numérique maximale d'une caractéristique (12 chiffres). */
    const val MAX_CHARACTERISTIC_VALUE = 999_999_999_999L

    /** [initial] : valeur de départ facultative de la jauge ; `null` ⇒ la jauge démarre à [max]. */
    fun validateGauge(
        label: String,
        min: Int,
        max: Int,
        increment: Int,
        initial: Int? = null
    ): Result<Unit> {
        val trimmed = label.trim()
        return when {
            trimmed.isEmpty() -> Result.failure(ValidationError("Gauge name cannot be empty"))
            trimmed.length > MAX_GAUGE_LABEL_LENGTH -> Result.failure(
                ValidationError("Gauge name must be $MAX_GAUGE_LABEL_LENGTH characters or less")
            )
            max <= min -> Result.failure(
                ValidationError("Gauge max value must be greater than its min value")
            )
            increment < 1 || increment > max -> Result.failure(
                ValidationError("Gauge increment must be between 1 and the max value")
            )
            initial != null && (initial < min || initial > max) -> Result.failure(
                ValidationError("Gauge initial value must be between its start and end values")
            )
            else -> Result.success(Unit)
        }
    }

    fun validateCharacteristic(
        label: String,
        value: Long,
        description: String? = null
    ): Result<Unit> {
        val trimmed = label.trim()
        return when {
            trimmed.isEmpty() -> Result.failure(ValidationError("Characteristic name cannot be empty"))
            trimmed.length > MAX_CHARACTERISTIC_LABEL_LENGTH -> Result.failure(
                ValidationError("Characteristic name must be $MAX_CHARACTERISTIC_LABEL_LENGTH characters or less")
            )
            value < 0 || value > MAX_CHARACTERISTIC_VALUE -> Result.failure(
                ValidationError("Characteristic value must be $MAX_CHARACTERISTIC_VALUE_DIGITS digits or less")
            )
            else -> validateDescription(description, "Characteristic")
        }
    }

    fun validateSkill(label: String, description: String? = null): Result<Unit> {
        val trimmed = label.trim()
        return when {
            trimmed.isEmpty() -> Result.failure(ValidationError("Skill cannot be empty"))
            trimmed.length > MAX_SKILL_LABEL_LENGTH -> Result.failure(
                ValidationError("Skill must be $MAX_SKILL_LABEL_LENGTH characters or less")
            )
            else -> validateDescription(description, "Skill")
        }
    }

    fun validatePower(label: String, description: String? = null): Result<Unit> {
        val trimmed = label.trim()
        return when {
            trimmed.isEmpty() -> Result.failure(ValidationError("Power cannot be empty"))
            trimmed.length > MAX_POWER_LABEL_LENGTH -> Result.failure(
                ValidationError("Power must be $MAX_POWER_LABEL_LENGTH characters or less")
            )
            else -> validateDescription(description, "Power")
        }
    }

    /** La description est facultative : absente ou vide, elle est toujours valide. */
    private fun validateDescription(description: String?, field: String): Result<Unit> {
        val trimmed = description?.trim().orEmpty()
        return if (trimmed.length > MAX_ENTRY_DESCRIPTION_LENGTH) {
            Result.failure(
                ValidationError("$field description must be $MAX_ENTRY_DESCRIPTION_LENGTH characters or less")
            )
        } else {
            Result.success(Unit)
        }
    }
}
